<template>
  <div class="modalFiles">
    <transition name="fade">
      <div v-show="!isModalOpened" ref="buttonRef" class="modalFiles__btn">
        <Button
          :class="[
            'modalFiles__btn-button',
            { 'modalFiles__btn-button-disabled': !hasFiles },
          ]"
          @click="openModal"
        >
          <div v-show="isIndikatorVisible" class="modalFiles__btn-status" />
          <Icon value="download" :ui="{ icon: 'modalFiles__btn-icon' }" />
        </Button>
      </div>
    </transition>

    <transition name="modal">
      <Modal
        v-show="isModalOpened"
        class="modalFiles__modal"
        :style="modalStyle"
      >
        <div class="modalFiles__modal-icons">
          <Icon
            value="tiny-close"
            :ui="{ icon: 'modalFiles__modal-icon' }"
            @click="collapseModal"
          />
        </div>
        <div class="modalFiles__modal-content">
          <p class="modalFiles__modal-title">
            История загрузки и формирования файлов
          </p>

          <transition-group
            name="fade"
            tag="div"
            class="modalFiles__modal-files"
          >
            <div
              v-for="file in tasks"
              :key="file.app_hash_md5"
              class="modalFiles__modal-file"
            >
              <div class="modalFiles__modal-info">
                <SvgImage
                  v-if="file.state !== 'SUCCESS' || !file.format"
                  value="book"
                  class="modalFiles__modal-format"
                />
                <SvgImage
                  v-else-if="file.format === 'pdf'"
                  value="pdf"
                  class="modalFiles__modal-format"
                />
                <SvgImage
                  v-else-if="file.format === 'pdf+xlsx'"
                  value="xlsx"
                  class="modalFiles__modal-format"
                />

                <div class="modalFiles__modal-loading">
                  <p class="modalFiles__modal-loading__title">
                    {{ file.file_name }}
                  </p>

                  <div
                    v-if="file.percent !== undefined"
                    class="modalFiles__modal-loading__progress-back"
                  >
                    <div
                      class="modalFiles__modal-loading__progress"
                      :style="{
                        width:
                          file.state === 'SUCCESS'
                            ? '100%'
                            : `${file.percent}%`,
                      }"
                    />
                  </div>

                  <p
                    v-if="file.total_files"
                    class="modalFiles__modal-loading__percent"
                  >
                    Загружено файлов:
                    {{ `${file.downloaded_files || 0}/${file.total_files}` }}
                  </p>

                  <div
                    v-else-if="file.percent !== undefined"
                    class="modalFiles__modal-loading__info"
                  >
                    <p class="modalFiles__modal-loading__percent">
                      {{ file.state === 'SUCCESS' ? '100' : file.percent }}%
                      загружено
                    </p>
                    <p class="modalFiles__modal-loading__percent">
                      <span style="color: var(--color-secondary-800)">
                        Статус задачи:
                      </span>
                      <span :class="getStatusClass(file.state)">
                        {{ getStatusText(file.state) }}
                      </span>
                    </p>
                  </div>
                </div>
              </div>

              <div class="modalFiles__modal-btns">
                <Icon
                  v-if="file.back_queue === 'print'"
                  value="download"
                  :ui="{
                    icon: [
                      'modalFiles__modal-download',
                      { 'icon-disabled': file.state !== 'SUCCESS' },
                    ],
                  }"
                  @click="downloadFiles(file)"
                />
                <Icon
                  v-if="file.back_queue === 'report'"
                  value="open-eye"
                  :ui="{
                    icon: [
                      'modalFiles__modal-download',
                      { 'icon-disabled': file.state !== 'SUCCESS' },
                    ],
                  }"
                  @click="getRoute(file)"
                />
                <!-- Отображать для всего, кроме процесса скачивания файлов -->
                <Icon
                  v-if="file.state === 'SUCCESS' || !file.total_files"
                  value="tiny-close"
                  :ui="{ icon: 'modalFiles__modal-close' }"
                  @click="openTaskDialog(file)"
                />
              </div>
            </div>
          </transition-group>
        </div>
      </Modal>
    </transition>

    <transition name="fade">
      <Modal v-if="isDialogTaskOpened" class="modalFiles__modal-dialog">
        <div class="modalFiles__modal-dialog-content">
          <p class="modalFiles__modal-title">Отменить задачу?</p>
          <div class="modalFiles__modal-dialog-btns">
            <Button view="outlined" @click="closeFiles(selectedAppHashMd5)">
              Да
            </Button>
            <Button @click="isDialogTaskOpened = false">Нет</Button>
          </div>
        </div>
      </Modal>
    </transition>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';
import { isNavigationFailure, useRoute, useRouter } from 'vue-router';
import { Button, Icon, SvgImage } from '~packages/epk-front-shared/ui';

import { usePrintService } from '@/shared/composables';
import { fileExtensions } from '@/shared/constants/file';
import mime, { type Mime } from '@/shared/constants/mime';
import setBlobApplicationType from '@/helpers/setBlobApplicationType';
import { useRpcClient, useRpcTasks } from '@/shared/api/rpc';
import type { FileItem, TypeFiles } from '@/shared/api/rpc/types';
import { download } from '@/shared/helpers';
import { useNotificationStore } from '@/shared/model';
import { Modal } from '@mkskom/components';

enum TaskStatus {
  PENDING = 'В очереди',
  start = 'Выполняется',
  working = 'В процессе',
  SUCCESS = 'Успешно завершено',
}

const router = useRouter();
const route = useRoute();
const { notifyError } = useNotificationStore();
const { blobFetchFromUrl } = usePrintService({});

const { rpcClient } = useRpcClient();
const { tasks } = useRpcTasks();

const isModalOpened = ref(false);
const isIndikatorVisible = ref(false);
const isDialogTaskOpened = ref(false);
const selectedAppHashMd5 = ref('');
const buttonRef = ref<HTMLElement | null>(null);

const tasksLength = ref();

const hasFiles = computed(() => tasks.value.length > 0);
const modalStyle = computed(() => {
  if (!buttonRef.value) return;
  const rect = buttonRef.value.getBoundingClientRect();
  return { transformOrigin: `${rect.left}px ${rect.top}px` };
});

watch(
  () => tasks.value.length,
  taskL => {
    if (taskL > tasksLength.value) openModal();
    tasksLength.value = taskL;
  }
);

const getStatusText = (state: string): string => {
  const text =
    TaskStatus[state as keyof typeof TaskStatus] ??
    'Ошибка. Перезапустите задачу';
  return text;
};

const getStatusClass = (state: string): string => {
  let cls = 'status-red';
  if (['PENDING', 'start', 'working'].includes(state)) cls = 'status-yellow';
  else if (state === 'SUCCESS') cls = 'status-green';
  return cls;
};

const openModal = () => {
  isModalOpened.value = true;
  isIndikatorVisible.value = false;
};

const collapseModal = () => {
  isModalOpened.value = false;
};

const openTaskDialog = (file: FileItem) => {
  selectedAppHashMd5.value = file.app_hash_md5;

  if (file.total_files || file.type === 'five') {
    closeFiles(file.app_hash_md5);
    return;
  }

  isDialogTaskOpened.value = true;
};

const closeFiles = async (id: string) => {
  if (!tasks.value) return;

  const index = tasks.value.findIndex(item => item.app_hash_md5 === id);
  if (index === -1) return;

  const task = tasks.value.find(item => item.app_hash_md5 === id);
  if (!task) return;

  await rpcClient.removeTask(task);

  isDialogTaskOpened.value = false;
};

const getRoute = async (file: FileItem) => {
  if (!file.name || !file.params || file.state !== 'SUCCESS') return;

  const { id: routeId, entity } = route.params;
  const query =
    entity && routeId
      ? { ...route.query, ref: entity, id: routeId }
      : undefined;

  const navigationResult = await router.push({
    name: file.name,
    params: file.params,
    query,
  });

  if (isNavigationFailure(navigationResult)) return;
  await closeFiles(file.app_hash_md5);
};

const downloadFiles = async (file: FileItem) => {
  const { app_hash_md5, files, file_name, format } = file;

  const downloadFile = async (
    fileUrl: string,
    ext: string,
    name: string,
    mimeType: Mime | undefined
  ) => {
    const blobData = await blobFetchFromUrl(fileUrl);
    if (!blobData.status) return false;
    const blob = setBlobApplicationType(blobData.data, mimeType);
    if (!blob) {
      notifyError('Не удалось получить ссылки для скачивания');
      return false;
    }
    await download(blob, `${name}.${ext}`, mimeType, false);
    return true;
  };

  const processFile = async (
    file: TypeFiles | string,
    name: string,
    task?: any
  ) => {
    const { pdf, xlsx, zip } = fileExtensions;

    if (typeof file === 'string') {
      return await downloadFile(file, pdf, file_name, mime.pdf);
    }

    if (format === `${pdf}+${xlsx}` || format === zip) {
      if (!task) {
        const total = file.xlsx ? 2 : 1;
        task = await rpcClient.createFileDownloadTask(file_name, total);
      }

      const pdfOk = await downloadFile(file.pdf, pdf, name, mime.pdf);
      task.updateProgress(pdfOk);

      if (file.xlsx) {
        const xlsxOk = await downloadFile(file.xlsx, xlsx, name, mime.xlsx);
        task.updateProgress(xlsxOk);
      }
    }
  };

  if (Array.isArray(files)) {
    const total = files.reduce(
      (acc, f) => acc + (f.result_download_url.xlsx ? 2 : 1),
      0
    );
    const task = await rpcClient.createFileDownloadTask(file_name, total);

    for (const f of files) {
      const [from, to] = f.meta.lists_current.split('_').map(Number);
      await processFile(
        f.result_download_url,
        `${file_name} с ${from + 1} по ${to}`,
        task
      );
    }
  } else {
    await processFile(files, file_name);
  }

  await closeFiles(app_hash_md5);
};

const getValueFromLocalStorage = () => {
  if (tasks.value) {
    tasksLength.value = tasks.value.length;

    if (tasks.value && !hasFiles.value) collapseModal();
  }
};

onMounted(() => {
  getValueFromLocalStorage();
});
</script>

<style lang="scss" scoped src="./ModalFiles.scss"></style>
